import {Component, OnInit, ViewChild} from '@angular/core';
import {ModalInterface} from "../../modal.interface";
import {Observable, Subject, forkJoin} from "rxjs";
import {ModalStateEvent} from "../../modal-state-event";
import {UntypedFormGroup} from "@angular/forms";
import {RxFormBuilder, RxwebValidators} from "@rxweb/reactive-form-validators";
import {ModalState} from "../../modal-state.enum";
import {TranslateService} from "@ngx-translate/core";
import {BugReportApiModel} from "../../../../api/models/bug-report.api-model";
import {plainToInstance} from "class-transformer";
import {ResponseNotificationService} from "../../../../core/services/response-notification.service";
import {BugReportAttachmentRestService} from "../../../../api/services/bug-report-attachment.rest.service";
import {BugReportAttachmentApiModel} from "../../../../api/models/bug-report-attachment.api-model";
import {BugReportRestService} from "../../../../api/services/bug-report.rest.service";
import {BUG_REPORT_ERROR_CONTEXT_FIELDS, BugReportErrorContext} from '../../../../api/models/bug-report-error-context.model';
import {FileUpload} from "primeng/fileupload";

@Component({
  selector: 'app-bug-report',
  templateUrl: './bug-report.component.html',
  styleUrls: ['./bug-report.component.scss'],
})
export class BugReportComponent implements ModalInterface, OnInit {
  data: {errorCode?: string | null; errorContext?: BugReportErrorContext | null};
  subject: Subject<ModalStateEvent>;
  state: Observable<ModalStateEvent>;
  public form: UntypedFormGroup;
  public attachments: {file: File; previewUrl: string | null}[] = [];
  public uploadProgress: number | null = null;
  public uploadMode: 'determinate' | 'indeterminate' = 'determinate';
  public errorContext: BugReportErrorContext | null = null;
  public readonly errorContextFields = BUG_REPORT_ERROR_CONTEXT_FIELDS;
  @ViewChild('uploader') public fileUploader?: FileUpload;

  constructor(
    private readonly fb: RxFormBuilder,
    private readonly translate: TranslateService,
    private readonly bugReportRS: BugReportRestService,
    private readonly attachmentRS: BugReportAttachmentRestService,
    private readonly rns: ResponseNotificationService
  ) {}

  ngOnInit(): void {
    this.errorContext = this.data?.errorContext ?? null;
    this.form = this.fb.group({
      code: [this.data?.errorCode ?? null, [RxwebValidators.maxLength({value: 64})]],
      topic: [null, [RxwebValidators.required(), RxwebValidators.minLength({value: 3}), RxwebValidators.maxLength({value: 255})]],
      content: [null, [RxwebValidators.required(), RxwebValidators.minLength({value: 3})]],
    });
    this.form.statusChanges.subscribe(() => this.subject.next(new ModalStateEvent(this.form.valid ? ModalState.VALID : ModalState.INVALID)));
    this.state.subscribe((mse: ModalStateEvent) => {
      if (mse.status === ModalState.PENDING) {
        this.submit();
      }
    });
  }

  public onFileSelect(event: {files?: File[]}): void {
    const files = Array.from(event?.files ?? []);
    const nextFiles = [...this.attachments, ...files.map(file => ({file, previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : null}))];
    if (nextFiles.length > 10) {
      this.rns.emitError(this.translate.instant('shared.modal.modal_service.report_bug.too_many_files'));
      this.fileUploader?.clear();
      return;
    }
    this.attachments = nextFiles;
    this.fileUploader?.clear();
  }

  public removeAttachment(index: number): void {
    const removed = this.attachments[index];
    if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
    this.attachments = this.attachments.filter((_, i) => i !== index);
  }

  public submit(): void {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      this.subject.next(new ModalStateEvent(ModalState.INVALID));
      return;
    }
    if (!this.attachments.length) {
      this.createReport([]);
      return;
    }
    this.uploadProgress = 0;
    forkJoin(this.attachments.map(attachment => this.attachmentRS.uploadWithProgress(attachment.file))).subscribe({
      next: results => this.createReport(results.filter((result): result is BugReportAttachmentApiModel => typeof result !== 'number')),
      error: () => {
        this.uploadProgress = null;
        this.rns.emitError(this.translate.instant('shared.modal.modal_service.report_bug.error_upload'));
      }
    });
  }

  private createReport(attachments: BugReportAttachmentApiModel[]): void {
    const report = plainToInstance(BugReportApiModel, this.form.value);
    report.errorContext = this.errorContext;
    report.attachments = attachments.map(attachment => attachment['@id']);
    this.bugReportRS.create(report).subscribe(created => {
      this.subject.next(new ModalStateEvent(ModalState.SUCCESS, created));
      this.rns.emitMessage(this.translate.instant('shared.modal.modal_service.report_bug.success'));
      this.attachments.forEach(attachment => attachment.previewUrl && URL.revokeObjectURL(attachment.previewUrl));
      this.attachments = [];
      this.uploadProgress = null;
    });
  }
}
